import { type ReactNode, useId } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { TeamMember, TeamRecipients } from './useTeamRecipients';

export const RECIPIENT_PICKER_COPY = {
  loading: 'Loading your team…',
  loadFailed: 'Couldn’t load your team.',
  retry: 'Try Again'
} as const;

/** The lines that name whose team it is. */
export interface RecipientPickerCopy {
  loading: string;
  loadFailed: string;
}

const SKELETON_ROWS = 4;

interface RecipientPickerProps {
  team: TeamRecipients;
  /** Locks the picker, e.g. while a send is in flight. */
  disabled?: boolean;
  /** The line under a search that matches nobody, given the trimmed query. */
  renderNoMatch: (query: string) => ReactNode;
  /** What a team with nobody eligible shows. */
  empty: ReactNode;
  /** In place of "your team", for a sender who is not on it. */
  copy?: RecipientPickerCopy;
}

/**
 * Who on the team gets the email (DV-3715): "Send To N of M", Select All /
 * Clear All, a search over name and email, and a checkbox per member. Shared
 * with Make Visible & Notify (DV-3563); the caller supplies the frame around
 * it and the copy that names where else to share.
 */
const RecipientPicker = ({
  team,
  disabled = false,
  renderNoMatch,
  empty,
  copy = RECIPIENT_PICKER_COPY
}: RecipientPickerProps) => {
  const ready = team.list.status === 'ready';
  const usable = ready && team.members.length > 0 && !disabled;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-3 px-3 pb-2 pt-2.5">
        <span className="text-sm">
          <span className="font-semibold">Send To</span>
          {ready && (
            <span className="text-muted-foreground">
              {' '}
              {team.picked.length} of {team.members.length}
            </span>
          )}
        </span>
        <Button
          type="button"
          variant="link"
          onClick={team.toggleAll}
          disabled={!usable}
          className="font-medium max-lg:min-h-11"
        >
          {team.allPicked ? 'Clear All' : 'Select All'}
        </Button>
      </div>
      <div className="px-3 pb-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="text"
            value={team.query}
            onChange={(event) => team.setQuery(event.target.value)}
            disabled={!usable}
            placeholder="Search Team Members"
            aria-label="Search team members"
            className="h-9 rounded-lg pl-9"
          />
        </div>
      </div>
      <div
        role="group"
        aria-label="Team members"
        aria-busy={team.list.status === 'loading'}
        className="flex max-h-44 flex-col overflow-y-auto px-1 pb-1"
      >
        <PickerBody
          team={team}
          disabled={disabled}
          renderNoMatch={renderNoMatch}
          empty={empty}
          copy={copy}
        />
      </div>
    </div>
  );
};

const PickerBody = ({
  team,
  disabled,
  renderNoMatch,
  empty,
  copy
}: Required<RecipientPickerProps>) => {
  const { list } = team;

  if (list.status === 'loading') {
    return (
      <>
        <span className="sr-only">{copy.loading}</span>
        {Array.from({ length: SKELETON_ROWS }, (_, row) => (
          <div key={row} aria-hidden className="flex items-center gap-3 px-2 py-1.5">
            <Skeleton className="h-4 w-4 rounded-sm" />
            <div className="flex flex-1 flex-col gap-1">
              <Skeleton className="h-3.5 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (list.status === 'failed') {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-2 py-3 text-sm text-muted-foreground">
        <span>{copy.loadFailed}</span>
        {list.retryable && (
          <Button type="button" variant="outline" size="sm" onClick={team.retry}>
            {RECIPIENT_PICKER_COPY.retry}
          </Button>
        )}
      </div>
    );
  }

  if (team.members.length === 0) {
    return <p className="m-0 px-2 py-3 text-sm text-muted-foreground">{empty}</p>;
  }

  if (team.shown.length === 0) {
    return (
      <p className="m-0 px-2 py-3 text-sm text-muted-foreground">
        {renderNoMatch(team.query.trim())}
      </p>
    );
  }

  return (
    <>
      {team.shown.map((member) => (
        <MemberRow
          key={member.userId}
          member={member}
          checked={team.isPicked(member.userId)}
          onToggle={() => team.toggle(member.userId)}
          disabled={disabled}
        />
      ))}
    </>
  );
};

interface MemberRowProps {
  member: TeamMember;
  checked: boolean;
  onToggle: () => void;
  disabled: boolean;
}

/** One member: the whole row is the checkbox's label, named by the member's
 *  name and described by their email. */
const MemberRow = ({ member, checked, onToggle, disabled }: MemberRowProps) => {
  const id = useId();
  return (
    <label
      htmlFor={`${id}-checkbox`}
      className="flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent has-[:disabled]:cursor-not-allowed has-[:disabled]:hover:bg-transparent max-lg:py-0"
    >
      <Checkbox
        id={`${id}-checkbox`}
        checked={checked}
        onCheckedChange={onToggle}
        disabled={disabled}
        aria-labelledby={`${id}-name`}
        aria-describedby={`${id}-email`}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span id={`${id}-name`} className="truncate text-sm font-medium">
          {member.name}
        </span>
        <span id={`${id}-email`} className="truncate text-xs text-muted-foreground">
          {member.email}
        </span>
      </span>
    </label>
  );
};

export default RecipientPicker;
